import React, { useEffect, useState, useRef } from "react";
import { Chart } from 'react-google-charts';
import moment from "moment";

import { downloadBlobFile, arrayToCsvFormat } from "common/helpers";
import NewFiltersBox from "views/FilterBox/NewFiltersBox";
import Button from "components/CustomButtons/Button.js";
import { API_BASE_URL } from 'common/constants';
import { fetchRequest } from "common/service";

const hrOptions = {
  legend: 'none',
  bar: { groupWidth: '95%' },
  candlestick: {
    fallingColor: { strokeWidth: 0, fill: '#0f9d58' }, 
    risingColor: { strokeWidth: 0, fill: '#ed193b' },  
  },
  hAxis: {
    title: 'Date',
    format: 'MMM dd',
    gridlines: { count: 0 },
    slantedText: false
  },
  vAxis: {
    title: 'Heart Rate (BPM)',
    minValue: 0,
    gridlines: { color: '#f0f0f0' }
  },
  backgroundColor: '#f8f9fa',
  chartArea: {
    backgroundColor: '#ffffff',
    left: '10%',
    top: '10%',
    width: '85%',
    height: '70%'
  }
};

const rrOptions = {
  legend: 'none',
  bar: { groupWidth: '95%' },
  candlestick: {
    fallingColor: { strokeWidth: 0, fill: '#0f9d58' }, 
    risingColor: { strokeWidth: 0, fill: '#3f51b5' },
  },
  hAxis: {
    title: 'Date',
    format: 'MMM dd',
    gridlines: { count: 0 },
    slantedText: false
  },
  vAxis: {
    title: 'Respiration Rate (BPM)',
    minValue: 0,
    gridlines: { color: '#f0f0f0' }
  },
  backgroundColor: '#f8f9fa',
  chartArea: {
    backgroundColor: '#ffffff',
    left: '10%',
    top: '10%',
    width: '85%',
    height: '70%'
  }
};

const Reports = () => {
  const [dataFetched, setDataFetched] = useState(false);
  const [dataDuration, setDataDuration] = useState(90); // Changed to 90 days initially
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [hrReports, setHrReports] = useState([]);
  const [rrReports, setRrReports] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  
  // New state variables for caching
  const [cachedData, setCachedData] = useState(null);
  const [lastFetchDate, setLastFetchDate] = useState(null);
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  const tabs = [
    { id: 7, label: '7' },
    { id: 14, label: '14' },
    { id: 30, label: '30' },
    { id: 90, label: '90' }
  ];

  const handleTabClick = (tabId) => {
    setDataDuration(tabId);
    console.log(`Tab ${tabId} clicked`);
    
    // Always filter from cache if available, regardless of user
    if (cachedData && cachedData.reports && cachedData.reports.length > 0) {
      console.log("Filtering from cache for tab:", tabId);
      filterFromCache(tabId);
    } else {
      console.log("No cached data available, fetching new data");
      // If no cached data, fetch data
      if (userInfo?.friendlyName) {
        fetchData(null);
      }
    }
  };

  // Helper function to check if we need to fetch new data
  const shouldFetchNewData = () => {
    if (!lastFetchDate) return true;
    
    const now = new Date();
    const lastFetch = new Date(lastFetchDate);
    const daysDifference = Math.floor((now - lastFetch) / (1000 * 60 * 60 * 24));
    
    // Fetch new data if more than 1 day has passed
    return daysDifference >= 1;
  };

  // Helper function to filter data from cache
  const filterFromCache = (duration) => {
    if (!cachedData || !cachedData.reports || cachedData.reports.length === 0) {
      console.log("No cached data available for filtering");
      setDataFetched(false);
      return;
    }
    
    filterFromCacheWithData(duration, cachedData.reports);
  };

  const filterFromCacheWithData = (duration, reports) => {
    if (!reports || reports.length === 0) {
      console.log("No reports provided for filtering");
      setDataFetched(false);
      return;
    }
    
    const now = new Date();
    const pastDate = new Date(now.getTime() - (duration * 24 * 60 * 60 * 1000));
    
    // Filter reports within the selected duration
    const filteredReports = reports.filter(report => {
      const reportDate = new Date(report.minValueProvidedOn);
      return reportDate >= pastDate && reportDate <= now;
    });
    
    console.log(`Filtered ${filteredReports.length} reports for ${duration} days`);
    
    prepareHeartRateReportsData(filteredReports);
    prepareRespirationRateReportsData(filteredReports);
  };

  // Helper function to get formatted date range for display
  const getFormattedDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getTime() - (dataDuration * 24 * 60 * 60 * 1000));
    
    return {
      start: startDate.toLocaleDateString(),
      end: now.toLocaleDateString()
    };
  };

  // Helper function to generate all dates in the selected range with month info
  const generateDateRange = () => {
    const dates = [];
    const now = new Date();
    let previousMonth = null;
    
    for (let i = dataDuration - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const fullDate = new Date(date);
      const currentMonth = date.getMonth();
      
      // Show month name on start and end dates
      // For 90 days, also show month name on month changes
      let displayText;
      const isFirstEntry = i === dataDuration - 1;
      const isLastEntry = i === 0;
      const isMonthChange = dataDuration === 90 && previousMonth !== null && previousMonth !== currentMonth;
      
      if (isFirstEntry || isLastEntry || isMonthChange) {
        displayText = `${month} ${day}`;
        console.log(`Showing month for ${displayText} - First: ${isFirstEntry}, Last: ${isLastEntry}, MonthChange: ${isMonthChange}, Duration: ${dataDuration}`);
      } else {
        displayText = day;
      }
      
      // Ensure last entry always shows month name
      if (i === 0) {
        displayText = `${month} ${day}`;
        console.log(`Forcing month display for last entry: ${displayText}`);
      }
      
      dates.push({
        day: day,
        displayText: displayText,
        fullDate: fullDate
      });
      
      previousMonth = currentMonth;
    }
    
    return dates;
  };

  // Helper function to create a map of existing data by full date
  const createDataMap = (reports, dataType) => {
    const dataMap = {};
    
    console.log(`Creating data map for ${dataType} with ${reports.length} reports`);
    
    for (let index = 0; index < reports.length; index++) {
      let data = reports?.[index]?.data;
      let timestamp = reports?.[index]?.minValueProvidedOn;
      
      if (!timestamp) {
        console.log("Skipping report with no timestamp:", reports[index]);
        continue;
      }
      
      // Create a unique key using the full date (YYYY-MM-DD format)
      const date = new Date(timestamp);
      const dateKey = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      if (dataType === 'hr') {
        let hrMax = data?.HrMax?.value || 0;
        let hrMin = data?.HrMin?.value || 0;
        let hrAvg = data?.HrAvg?.value || 0;
        
        if (hrMax !== 0 && hrMin !== 0 && hrAvg !== 0) {
          dataMap[dateKey] = {
            min: hrMin,
            avg: hrAvg,
            max: hrMax
          };
          console.log(`Added HR data for ${dateKey}: min=${hrMin}, avg=${hrAvg}, max=${hrMax}`);
        }
      } else if (dataType === 'rr') {
        let rrMax = data?.RrMax?.value || 0;
        let rrMin = data?.RrMin?.value || 0;
        let rrAvg = data?.RrAvg?.value || 0;
        
        if (rrMax !== 0 && rrMin !== 0 && rrAvg !== 0) {
          dataMap[dateKey] = {
            min: rrMin,
            avg: rrAvg,
            max: rrMax
          };
          console.log(`Added RR data for ${dateKey}: min=${rrMin}, avg=${rrAvg}, max=${rrMax}`);
        }
      }
    }
    
    console.log(`Data map created with ${Object.keys(dataMap).length} entries for ${dataType}`);
    console.log(`Data map keys:`, Object.keys(dataMap).sort());
    return dataMap;
  };

  const prepareHeartRateReportsData = (reports) => {
    if (!reports || reports.length === 0) {
      console.log("No reports provided for HR data preparation");
      setHrReports([['Date', 'Low', 'Open', 'Close', 'High']]);
      return;
    }

    const heartRateReportsData = [['Date', 'Low', 'Open', 'Close', 'High']];
    const allDates = generateDateRange();
    const dataMap = createDataMap(reports, 'hr');
    
    console.log("Preparing HR data with", reports.length, "reports and data as", dataMap);
    
    allDates.forEach(dateObj => {
      // Create the date key in YYYY-MM-DD format to match the dataMap keys
      const dateKey = dateObj.fullDate.toISOString().split('T')[0];
      const dayData = dataMap[dateKey];
      
      console.log(`Checking date ${dateKey} for HR data:`, dayData ? 'Found' : 'Not found');
      
      if (dayData) {
        const dataObject = [
          dateObj.displayText, 
          dayData.min, 
          dayData.avg - 0.5, 
          dayData.avg + 0.5, 
          dayData.max
        ];
        heartRateReportsData.push(dataObject);
      } else {
        // If no data for this day, use small non-zero values to make them visible
        const dataObject = [
          dateObj.displayText, 
          0.1, 
          0.1, 
          0.1, 
          0.1
        ];
        heartRateReportsData.push(dataObject);
      }
    });
    
    console.log("HR data prepared:", heartRateReportsData, "rows");
    setHrReports(heartRateReportsData);
    
    // Ensure dataFetched is set to true when we have data
    if (heartRateReportsData.length > 1) {
      console.log("Setting dataFetched to true for HR data");
      setDataFetched(true);
    }
  };

  const prepareRespirationRateReportsData = (reports) => {
    if (!reports || reports.length === 0) {
      console.log("No reports provided for RR data preparation");
      setRrReports([['Date', 'Low', 'Open', 'Close', 'High']]);
      return;
    }

    const respirationRateReportsData = [['Date', 'Low', 'Open', 'Close', 'High']];
    const allDates = generateDateRange();
    const dataMap = createDataMap(reports, 'rr');
    
    console.log("Preparing RR data with", reports.length, "reports");
    
    allDates.forEach(dateObj => {
      // Create the date key in YYYY-MM-DD format to match the dataMap keys
      const dateKey = dateObj.fullDate.toISOString().split('T')[0];
      const dayData = dataMap[dateKey];
      
      console.log(`Checking date ${dateKey} for RR data:`, dayData ? 'Found' : 'Not found');
      
      if (dayData) {
        const dataObject = [
          dateObj.displayText, 
          dayData.min, 
          dayData.avg - 0.5, 
          dayData.avg + 0.5, 
          dayData.max
        ];
        respirationRateReportsData.push(dataObject);
      } else {
        // If no data for this day, use small non-zero values to make them visible
        const dataObject = [
          dateObj.displayText, 
          0.1, 
          0.1, 
          0.1, 
          0.1
        ];
        respirationRateReportsData.push(dataObject);
      }
    });
    
    console.log("RR data prepared:", respirationRateReportsData.length, "rows");
    setRrReports(respirationRateReportsData);
    
    // Ensure dataFetched is set to true when we have data
    if (respirationRateReportsData.length > 1) {
      console.log("Setting dataFetched to true for RR data");
      setDataFetched(true);
    }
  };

  const fetchData = async (fetchedUserInfo = null) => {
    if(fetchedUserInfo && Object.keys(fetchedUserInfo).length > 0) {
      setUserInfo(fetchedUserInfo);
    }

    console.log("fetchData called with userInfo:", userInfo?.friendlyName);
    try {
      if(userInfo?.friendlyName !== "all" && userInfo?.friendlyName) {
        const needsNewData = shouldFetchNewData() || !cachedData || cachedData.friendlyName !== userInfo?.friendlyName || isInitialFetch;
        
        console.log("Needs new data:", needsNewData, "Initial fetch:", isInitialFetch);
        
        if (needsNewData) {
          setDataFetched(false);
          setIsLoading(true);
          const endPoint = `${API_BASE_URL}/data/devicedata-advanced?sortBy=data.TimestampPs.value`;
          
          const now = new Date();
          const currentDate = now.toISOString();
          
          const pastDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
          const pastDateISO = pastDate.toISOString();
          
          const payload = {
            "deviceDataModelId": "296f0b26-f21f-4847-8b7d-b9ed80b860df",
            "deviceCriteria": [
                { "key": "DevidPs", "operator": "Equal", "value": userInfo?.friendlyName},
                { "key": "TimestampPs", "operator": "Between", "valueFrom": pastDateISO, "valueTo": currentDate }
            ]
          };
          
          const sensorData = await fetchRequest("POST", endPoint, payload);
          const reports = sensorData?.content || [];
          console.log(reports, "Reports to cache:", reports.length);
          
          const newCachedData = {
            reports: reports,
            friendlyName: userInfo?.friendlyName,
            fetchDate: now.toISOString()
          };
          
          setCachedData(newCachedData);
          setLastFetchDate(now.toISOString());
          setIsInitialFetch(false);
          
          filterFromCacheWithData(dataDuration, newCachedData.reports);
        } else {
          filterFromCache(dataDuration);
        }
        
        setIsLoading(false);
        setErrorMsg("");
      } else {
        setErrorMsg("Please select atleast one device to fetch data");
        setDataFetched(false);
      }
    } catch (error) {
      console.error('Error in getLastPVitalsForSensor:', error);
      setIsLoading(false);
      setDataFetched(false);
      throw error;
    }
  }

  // Download CSV data
  const downloadCSVData = () => {
    if (!cachedData || !cachedData.reports || cachedData.reports.length === 0) {
      alert("No data found to download");
      return;
    }

    const headers = [
      "Date",
      "Heart_Rate_Min",
      "Heart_Rate_Avg", 
      "Heart_Rate_Max",
      "Respiration_Rate_Min",
      "Respiration_Rate_Avg",
      "Respiration_Rate_Max"
    ];

    const csvData = [];
    
    // Generate date range for the current duration
    const allDates = generateDateRange();
    const hrDataMap = createDataMap(cachedData.reports, 'hr');
    const rrDataMap = createDataMap(cachedData.reports, 'rr');

    allDates.forEach(dateObj => {
      const dateKey = dateObj.fullDate.toISOString().split('T')[0];
      const hrData = hrDataMap[dateKey];
      const rrData = rrDataMap[dateKey];
      
      csvData.push({
        Date: dateObj.fullDate.toLocaleDateString(),
        "Heart_Rate_Min": hrData?.min || '--',
        "Heart_Rate_Avg": hrData?.avg || '--',
        "Heart_Rate_Max": hrData?.max || '--',
        "Respiration_Rate_Min": rrData?.min || '--',
        "Respiration_Rate_Avg": rrData?.avg || '--',
        "Respiration_Rate_Max": rrData?.max || '--'
      });
    });

    downloadBlobFile(
      arrayToCsvFormat(csvData, headers), 
      `reports_data_${dataDuration}_days_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`, 
      "text/csv;charset=utf-8;"
    );
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (userInfo?.friendlyName && userInfo?.friendlyName !== "all") {
      setDataFetched(false);
      setHrReports([]);
      setRrReports([]);
      fetchData(null);
    }
  }, [userInfo]);

  useEffect(() => {
    if (cachedData && cachedData.reports && cachedData.reports.length > 0) {
      console.log("Filtering cached data for new duration:", dataDuration);
      filterFromCache(dataDuration);
    }
  }, [dataDuration, cachedData]);

  return (
    <div>
      <NewFiltersBox onApply={fetchData} friendlyNameHelperTextLabel={"Select atleast one device to fetch data"} />
      
      {/* Download CSV Button */}
      {userInfo && userInfo?.friendlyName !== "all" && cachedData && cachedData.reports && cachedData.reports.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px'
        }}>
          <Button 
            color="primary" 
            onClick={downloadCSVData}
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            Download CSV
          </Button>
        </div>
      )}
      
      {/* Tabs Container */}
      {userInfo && userInfo?.friendlyName !== "all" && <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        gap: '20px'
      }}>
        {/* Left side - Status and Refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Loading Indicator */}
          {isLoading && (
            <div style={{
              fontSize: '14px',
              color: '#2196f3',
              padding: '4px 8px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              border: '1px solid #2196f3'
            }}>
              ⏳ Loading data...
            </div>
          )}
        </div>

        {/* Right side - Tabs */}
        <div style={{
          display: 'flex',
          border: '1px solid #ccc',
          borderRadius: '4px',
          overflow: 'hidden',
          width: 'fit-content',
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? 'none' : 'auto'
        }}>
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={isLoading ? undefined : () => handleTabClick(tab.id)}
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 20px',
                cursor: isLoading ? 'default' : 'pointer',
                backgroundColor: dataDuration === tab.id ? '#f44336' : 'white',
                color: dataDuration === tab.id ? 'white' : 'black',
                borderRight: tab.id < tabs.length - 1 ? '1px solid #ccc' : 'none',
                fontWeight: dataDuration === tab.id ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            >
              {tab.label} {dataDuration === tab.id && 'Days'}
            </div>
          ))}
        </div>
      </div>}

      {errorMsg && <div style={{display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px", fontWeight: "bold"}}>
        <span>{errorMsg}</span>
      </div>}

      {dataFetched && hrReports?.length > 1 && <>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <h3>Heart Rate Reports</h3>
          <label>Date Range: {getFormattedDateRange().start} to {getFormattedDateRange().end}</label>
        </div>
        <Chart
          chartType="CandlestickChart"
          width="100%"
          height="400px"
          data={hrReports}
          options={hrOptions}
        />
      </>}
      {dataFetched && rrReports?.length > 1 && <>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <h3>Respiration Rate Reports</h3>
          <label>Date Range: {getFormattedDateRange().start} to {getFormattedDateRange().end}</label>
        </div>
        <Chart
          chartType="CandlestickChart"
          width="100%"
          height="400px"
          data={rrReports}
          options={rrOptions}
        />
      </>}
    </div>
  );
};

export default Reports;